import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { resetPageNumbers, updateSearchFilters } from 'js/actions';
import FlowDetailViewContainer from 'js/components/data_flows/cards/FlowDetailViewContainer';
import SourcePipeline from 'js/components/pipelines/SourcePipelineContainer';
import { getResourceEditModalOpen } from 'js/reducers/app/selectors';
import { getFlowMetricsInterval } from 'js/reducers/metrics/selectors';
import { POLLING_INTERVAL_MS } from 'js/constants/config';
import { ROUTE_PIPELINES } from 'js/constants/pathnames';
import { SearchFilterViews } from 'js/constants/visibilityFilters';
import { useDrawerContext } from 'new/components/drawer/useDrawerContext';
import { EditBaseFlowEmbedded } from 'new/components/flows/types/base/EditBaseFlowEmbedded';
import { DraftContextProvider } from 'new/components/flows/types/genai_rag/context/Draft/DraftContextProvider';
import { DrawerWithNewDataFlowButton } from 'new/components/integrate/DrawerWithNewDataFlowButton';
import { ROUTE_CONNECT, ROUTE_PROJECTS } from 'new/constants/pathnames';
import { ConnectionLoader } from 'new/elements/ConnectionLoader/ConnectionLoader';
import { ModalAddToMarketplace } from 'new/elements/ModalAddToMarketplace/ModalAddToMarketplace';
import { ModalFilePreview } from 'new/elements/ModalFilePreview';
import { ModalMoveToProject } from 'new/elements/ModalMoveToProject';
import { ModalReingest } from 'new/elements/ModalReingest';
import { Stack } from 'new/elements/Stack';
import { useAppContext } from 'new/hooks/useAppContext';
import { ScreenConfig } from 'new/routes/RouterManager';
import useEntities from 'new/state/entities/repository/useEntities';
import useMetricsAdapter from 'new/state/metrics/adapter/useMetricsAdapter';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';
import { useEditBaseFlow } from 'new/components/flows/types/base/useEditBaseFlow';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { EditFlowName } from '../../EditFlowName/EditFlowName';
import { DraftConfirmationDialog } from '../genai_rag/components/DraftConfirmationDialog';

import S from './EditBaseFlow.module.scss';
import useAppDispatch from 'js/hooks/useAppDispatch';

const EditBaseFlow = () => {
  const { setSubHeader, setCurrentSlug } = useAppContext();
  const dispatch = useAppDispatch();
  const { requestFlowMetrics } = useMetricsAdapter();
  const { refreshPipelineById } = usePipelinesAdapter();
  const { id: rootNodeId, type: flowType } = useParams<any>();
  const onRootRef = useRef<HTMLDivElement>(null);
  const drawerContext = useDrawerContext();
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const hasLoadedRef = useRef(false);
  const prevRootNodeIdRef = useRef(rootNodeId);

  const { data: entities } = useEntities();
  const { resource, projectId } = useEditBaseFlow(rootNodeId);

  // Get selected card ID from Redux state to conditionally render the detail drawer
  const selectedCardID = useSelector((state: any) => state.pipelines?.detailView?.nodeKey);
  const resourceEditModalOpen = useSelector(getResourceEditModalOpen);
  const flowMetricsInterval = useSelector(getFlowMetricsInterval);

  const flow = entities.flowNodes[rootNodeId];

  // Read the toggle interval through a ref so refreshPipeline keeps a STABLE
  // identity. The toolbar refresh button lives behind SourcePipeline's memo
  // comparator, which ignores the refreshPipeline prop — so a changing callback
  // identity never reaches the button, leaving it bound to the first closure
  // (default interval = latest run) and ignoring the toggle. Reading the ref at
  // call-time means the button always uses the current Latest Run / Lifetime
  // selection. The adapter then resolves run_id (latest run) vs lifetime, and
  // already coerces excluded source types to lifetime. See NEX-21823.
  const flowMetricsIntervalRef = useRef(flowMetricsInterval);
  flowMetricsIntervalRef.current = flowMetricsInterval;

  const refreshPipeline = useCallback(() => {
    // Pass sourceId + the current run-scope interval so refresh refetches metrics
    // honoring the toggle. Without sourceId the adapter short-circuits the
    // latestRun path and fires no metrics request at all.
    const sourceId = flow?.data_source_id ?? null;
    refreshPipelineById(rootNodeId);
    requestFlowMetrics(rootNodeId, sourceId, { interval: flowMetricsIntervalRef.current });
  }, [rootNodeId, flow?.data_source_id, requestFlowMetrics]);

  // Clear stale Redux state on mount to prevent stale filters/page from persisting
  // This ensures that when navigating to edit pages, any stale state from list views
  // (like page=3 from the flows list) doesn't get applied when clicking breadcrumb back
  useEffect(() => {
    dispatch(resetPageNumbers());
    dispatch(updateSearchFilters(SearchFilterViews.PIPELINES, []));
    dispatch(updateSearchFilters(SearchFilterViews.PROJECT_PIPELINES, []));
  }, [dispatch]);

  // Initialize pipeline on mount and set up 30-second auto-refresh timer
  useEffect(() => {
    drawerContext?.toggleCollapse(false);

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (!rootNodeId || resourceEditModalOpen) return;

    refreshPipeline();
    timerRef.current = setInterval(refreshPipeline, POLLING_INTERVAL_MS);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [rootNodeId, resourceEditModalOpen, refreshPipeline, drawerContext]);

  const subHeaderConfig = useMemo(() => {
    if (!rootNodeId || !flowType) return null;

    if (projectId) {
      // Flow belongs to a project - use project breadcrumb path
      const projectName = entities.projects[projectId]?.name || null;
      const projectDisplayName = projectName || 'Project ID: ' + String(projectId);
      return {
        slugs: ['integrate', 'projects', projectDisplayName, String(rootNodeId)],
        breadcrumbList: [
          `${ROUTE_CONNECT}`,
          `${ROUTE_PROJECTS}`,
          `${ROUTE_PROJECTS}/${projectId}`,
          '',
        ],
      };
    }
    // Flow doesn't belong to a project - use standard flow breadcrumb path
    return {
      slugs: ['integrate', 'flows', String(rootNodeId)],
      breadcrumbList: [`${ROUTE_CONNECT}`, `${ROUTE_CONNECT}${ROUTE_PIPELINES}`, ''],
    };
  }, [rootNodeId, flowType, projectId, entities.projects]);

  // Set the subheader config - this will persist unless overridden
  useEffect(() => {
    if (subHeaderConfig) {
      setSubHeader(subHeaderConfig);
      setCurrentSlug(rootNodeId);
    }
  }, [subHeaderConfig, rootNodeId, setSubHeader, setCurrentSlug]);

  if (prevRootNodeIdRef.current !== rootNodeId) {
    hasLoadedRef.current = false;
    prevRootNodeIdRef.current = rootNodeId;
  }

  if (resource && rootNodeId && flowType) {
    hasLoadedRef.current = true;
  }

  if ((!resource || !rootNodeId || !flowType) && !hasLoadedRef.current) {
    return <ConnectionLoader loading message="Loading Flow" />;
  }

  if (!resource || !rootNodeId || !flowType) {
    return null;
  }

  return (
    <DraftContextProvider flowId={rootNodeId}>
      <Stack ref={onRootRef} flex={1} height="100%" position="relative">
        <EditFlowName flowId={rootNodeId} />
        <div className={S.sourcePipelineContainer}>
          <SourcePipeline
            refreshPipeline={refreshPipeline}
            onRootRef={onRootRef}
            dataSource={resource}
            fullScreen
          />
        </div>
      </Stack>
      {selectedCardID && <FlowDetailViewContainer />}
      <DraftConfirmationDialog />
    </DraftContextProvider>
  );
};

const EditBaseFlowWrapper = () => {
  const { isEmbeddedMode } = useAppContext();

  if (isEmbeddedMode) {
    return <EditBaseFlowEmbedded />;
  }

  return (
    <DrawerWithNewDataFlowButton>
      <EditBaseFlow />
    </DrawerWithNewDataFlowButton>
  );
};

EditBaseFlowWrapper.screenConfig = {
  screenName: 'EditBaseFlow',
  domain: 'main',
  path: [`${ROUTE_CONNECT}${ROUTE_PIPELINES}/:type/:id/edit`],
  pageTitle: 'Edit Flow',
  modals: [ModalMoveToProject, ModalReingest, ModalFilePreview, ModalAddToMarketplace],
  manageBreadcrumbs: true, // Signal that this component manages its own breadcrumbs
  header: {},
} as ScreenConfig<any>;

export default EditBaseFlowWrapper;
